import { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { markCacheResetCompleted } from '@/hooks/useScheduledCacheReset';

interface CacheResetButtonProps {
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  children?: React.ReactNode;
  showIcon?: boolean;
}

export const CacheResetButton = ({ 
  variant = 'default', 
  size = 'default',
  className = '',
  children,
  showIcon = true
}: CacheResetButtonProps) => {
  const [isResetting, setIsResetting] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const performReset = async () => {
    setIsResetting(true);
    
    try {
      // 1. React Query — остановить и очистить
      await queryClient.cancelQueries();
      queryClient.clear();
      
      // 2. Service Workers
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
      }
      
      // 3. Cache API
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map(n => caches.delete(n)));
      }
      
      // 4. IndexedDB
      if ('indexedDB' in window && indexedDB.databases) {
        const dbs = await indexedDB.databases();
        dbs.forEach(db => db.name && indexedDB.deleteDatabase(db.name));
      }
      
      // 5. Cookies
      document.cookie.split(';').forEach(c => {
        const name = c.split('=')[0].trim();
        if (name) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        }
      });
      
      // 6. localStorage — сохранить критичные ключи
      const keysToPreserve = [
        'hotel3d-ui-theme',
        'hotel3d-language',
        'i18nextLng',
        'selected_hotel_id',
        'super_admin_selected_hotel',
        'scheduled_cache_reset',
      ];
      // Динамически: Supabase auth tokens
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
          keysToPreserve.push(key);
        }
        if (key === 'supabase.auth.token') {
          keysToPreserve.push(key);
        }
      });
      
      const saved: Record<string, string | null> = {};
      keysToPreserve.forEach(k => { saved[k] = localStorage.getItem(k); });
      localStorage.clear();
      Object.entries(saved).forEach(([k, v]) => { if (v) localStorage.setItem(k, v); });
      
      // 7. sessionStorage
      sessionStorage.clear();
      
      // 8. Планировщик
      markCacheResetCompleted();
      
      // 9. Флаг успеха (для toast ПОСЛЕ reload)
      localStorage.setItem('cache_reset_success', Date.now().toString());
      
      // 10. Обход HTTP Disk Cache (параллельно)
      await Promise.all([
        fetch('/index.html', { cache: 'reload' }).catch(() => {}),
        fetch('/version.json', { cache: 'reload' }).catch(() => {}),
      ]);
      
      // 11. МГНОВЕННАЯ перезагрузка на текущей странице
      window.location.reload();
      
    } catch (error) {
      console.error('Cache reset error:', error);
      toast({
        title: "Ошибка",
        description: "Не удалось очистить кэш. Попробуйте обновить страницу вручную.",
        variant: "destructive",
      });
      setIsResetting(false);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button 
          variant={variant} 
          size={size}
          className={className}
          disabled={isResetting}
        >
          {showIcon && <RefreshCw className={`h-4 w-4 ${children ? 'mr-2' : ''} ${isResetting ? 'animate-spin' : ''}`} />}
          {children}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Обновить приложение?</AlertDialogTitle>
          <AlertDialogDescription>
            Это действие очистит кэш приложения и перезагрузит страницу. 
            Все несохраненные данные будут потеряны.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Отмена</AlertDialogCancel>
          <AlertDialogAction 
            onClick={performReset}
            className="bg-green-600 hover:bg-green-700"
          >
            Обновить
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
